import { Injectable } from '@angular/core';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { environment } from '@env/environment';

@Injectable()
export class LoggingService {
  appInsights: ApplicationInsights;
  constructor() {
    this.appInsights = new ApplicationInsights({
      config: {
        instrumentationKey: environment.appInsights.instrumentationKey,
        enableAutoRouteTracking: true // option to log all route changes
      },
    });

    // --- Telemetry Initializer: filters chunk-load and extension-originated errors ---
    const telemetryInitializer = (envelope: any) => {
      try {
        // Set cloud role tags
        envelope.tags = envelope.tags || {};
        envelope.tags['ai.cloud.role'] = 'Client Portal';
        envelope.tags['ai.cloud.roleInstance'] = 'Client Portal1';

        // 0) Existing filter: ignore chunk load errors
        const msg = safeToString(envelope);
        if (msg.includes('Loading chunk') || msg.includes('ChunkLoadError')) {
          return false;
        }

        // Helper accessors
        const baseType = envelope?.baseType || '';
        const baseData = envelope?.data?.baseData || {};

        // 1) If the URL itself is a chrome extension URL, drop it
        const url = baseData?.url || envelope?.tags?.['ai.operation.name'] || '';
        if (isChromeExtensionUrl(url)) {
          return false;
        }

        // 2) For Exception telemetry: check parsedStack frames for chrome-extension origins
        if (isExceptionEnvelope(baseType, baseData)) {
          const exceptions = baseData.exceptions || [];
          const first = exceptions[0] || {};
          const frames = Array.isArray(first.parsedStack) ? first.parsedStack : [];

          if (frames.some((f: any) => isChromeExtensionUrl(f?.fileName))) {
            return false;
          }

          const hasExtInRaw =
            typeof first.stack === 'string' && first.stack.includes('chrome-extension://');
          if (hasExtInRaw) {
            return false;
          }
        }

        if (isTraceEnvelope(baseType, baseData)) {
          const traceMsg: string = baseData.message || '';
          if (traceMsg.includes('chrome-extension://')) {
            return false;
          }
        }

        if (isPageViewEnvelope(baseType, baseData)) {
          if (isChromeExtensionUrl(baseData.uri) || isChromeExtensionUrl(baseData.referrer)) {
            return false;
          }
        }

        return true;
      } catch {
        return true;
      }
    };

    this.appInsights.addTelemetryInitializer(telemetryInitializer);
    this.appInsights.loadAppInsights();
  }

  logPageView(name?: string, url?: string) {
    this.appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this.appInsights.trackEvent({ name: name }, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this.appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(exception: Error, severityLevel?: number) {
    this.appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this.appInsights.trackTrace({ message: message }, properties);
  }
}

function safeToString(envelope: any): string {
  try {
    // Prefer baseData message/exception if present to avoid noisy JSON
    const baseData = envelope?.data?.baseData;
    if (baseData?.message) return String(baseData.message);
    if (Array.isArray(baseData?.exceptions) && baseData.exceptions[0]?.message) {
      return String(baseData.exceptions[0].message);
    }
    // fallback
    return String(envelope);
  } catch {
    return '';
  }
}

function isChromeExtensionUrl(v: any): boolean {
  const s = String(v || '');
  return s.startsWith('chrome-extension://');
}

function isExceptionEnvelope(baseType: string, baseData: any): boolean {
  // Base type is usually 'ExceptionData' for exceptions
  return baseType === 'ExceptionData' || !!baseData?.exceptions;
}

function isTraceEnvelope(baseType: string, baseData: any): boolean {
  // Base type is usually 'MessageData' for traces
  return baseType === 'MessageData' || typeof baseData?.message === 'string';
}

function isPageViewEnvelope(baseType: string, baseData: any): boolean {
  // Base type is usually 'PageviewData' or 'PageviewPerformanceData'
  return baseType?.startsWith('Pageview');
}
